// src/components/games/ColorMatchGame.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiAward, FiTrendingUp } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const COLORS = [
  { name: 'أحمر', color: '#ef4444', nameColor: '#dc2626' },
  { name: 'أزرق', color: '#3b82f6', nameColor: '#2563eb' },
  { name: 'أخضر', color: '#22c55e', nameColor: '#16a34a' },
  { name: 'أصفر', color: '#eab308', nameColor: '#ca8a04' },
  { name: 'بنفسجي', color: '#a855f7', nameColor: '#9333ea' },
  { name: 'برتقالي', color: '#f97316', nameColor: '#ea580c' },
  { name: 'وردي', color: '#ec4899', nameColor: '#db2777' },
  { name: 'بني', color: '#92400e', nameColor: '#78350f' }
];

const ColorMatchGame = ({ onBack }) => {
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [currentColor, setCurrentColor] = useState(null);
  const [displayColor, setDisplayColor] = useState(null);
  const [options, setOptions] = useState([]);
  const [bestScore, setBestScore] = useState(() => {
    return parseInt(localStorage.getItem('colorMatchBest')) || 0;
  });
  const [streak, setStreak] = useState(0);
  const [difficulty, setDifficulty] = useState('easy'); // easy, medium, hard

  const generateRound = () => {
    const randomColor = COLORS[Math.floor(Math.random() * COLORS.length)];
    const wrongColor = COLORS.filter(c => c.name !== randomColor.name)[Math.floor(Math.random() * (COLORS.length - 1))];
    
    // اللون المعروض: إما اسم اللون بلون آخر أو اسم اللون الصحيح
    const isConflict = difficulty !== 'easy' && Math.random() > 0.5;
    
    setCurrentColor(randomColor);
    setDisplayColor(isConflict ? wrongColor : randomColor);
    
    // توليد الخيارات
    const correctAnswer = randomColor.name;
    const allOptions = COLORS.map(c => c.name).filter(n => n !== correctAnswer);
    const wrongOptions = allOptions.sort(() => Math.random() - 0.5).slice(0, 3);
    const allAnswers = [correctAnswer, ...wrongOptions].sort(() => Math.random() - 0.5);
    
    setOptions(allAnswers);
  };

  const startGame = () => {
    setScore(0);
    setTimeLeft(30);
    setGameStarted(true);
    setGameOver(false);
    setStreak(0);
    generateRound();
  };

  useEffect(() => {
    if (gameStarted && timeLeft > 0 && !gameOver) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameStarted) {
      setGameOver(true);
      setGameStarted(false);
      
      if (score > bestScore) {
        setBestScore(score);
        localStorage.setItem('colorMatchBest', score.toString());
        toast.success('🎉 رقم قياسي جديد!');
      }
    }
  }, [timeLeft, gameStarted, gameOver, score, bestScore]);

  const handleAnswer = (selectedColor) => {
    if (selectedColor === currentColor.name) {
      const points = difficulty === 'hard' ? 3 : difficulty === 'medium' ? 2 : 1;
      setScore(score + points);
      setStreak(streak + 1);
      setTimeLeft(timeLeft + 2); // مكافأة وقت
      toast.success(`صحيح! +${points}`);
    } else {
      setStreak(0);
      setTimeLeft(Math.max(0, timeLeft - 3)); // عقوبة وقت
      toast.error('خطأ! -3 ثواني');
    }
    generateRound();
  };

  if (!gameStarted && !gameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-orange-100"
          >
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">🎨</div>
              <h2 className="text-3xl font-black text-gray-900 mb-2">مطابقة الألوان</h2>
              <p className="text-gray-600">اختر اللون الصحيح بأسرع وقت!</p>
            </div>

            {/* Difficulty Selection */}
            <div className="mb-6">
              <p className="text-sm font-bold text-gray-700 mb-3">اختر المستوى:</p>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`py-3 rounded-xl font-bold text-sm transition ${
                    difficulty === 'easy'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  سهل
                </button>
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`py-3 rounded-xl font-bold text-sm transition ${
                    difficulty === 'medium'
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  متوسط
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`py-3 rounded-xl font-bold text-sm transition ${
                    difficulty === 'hard'
                      ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  صعب
                </button>
              </div>
            </div>

            {bestScore > 0 && (
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 mb-6 border-2 border-yellow-200">
                <div className="flex items-center justify-center gap-2">
                  <FiAward className="text-yellow-600" size={24} />
                  <div>
                    <div className="text-xs font-bold text-gray-600">أفضل نتيجة</div>
                    <div className="text-2xl font-black text-gray-900">{bestScore}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={startGame}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                ابدأ اللعب
              </button>

              <button
                onClick={onBack}
                className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                <span className="flex items-center justify-center gap-2">
                  <FiArrowLeft size={20} />
                  رجوع
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white rounded-3xl p-8 shadow-2xl border-2 border-orange-100"
        >
          <div className="text-center">
            <div className="text-6xl mb-4">{score > bestScore ? '🏆' : '🎯'}</div>
            <h2 className="text-3xl font-black text-gray-900 mb-2">
              {score > bestScore ? 'رقم قياسي جديد!' : 'انتهت اللعبة!'}
            </h2>
            <div className="text-5xl font-black text-orange-600 mb-6">{score}</div>
            
            {score > bestScore && (
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-4 mb-6 border-2 border-yellow-200">
                <p className="text-sm font-bold text-gray-700">
                  تجاوزت الرقم القياسي السابق: {bestScore}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={startGame}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition"
              >
                العب مرة أخرى
              </button>
              <button
                onClick={onBack}
                className="w-full bg-gray-100 text-gray-700 py-4 rounded-xl font-bold hover:bg-gray-200 transition"
              >
                رجوع
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 p-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              setGameStarted(false);
              setGameOver(false);
            }}
            className="flex items-center gap-2 bg-white px-4 py-2 rounded-xl shadow-md hover:shadow-lg transition font-bold text-gray-700"
          >
            <FiArrowLeft size={18} />
            <span className="text-sm">إنهاء</span>
          </button>

          <h1 className="text-2xl font-black text-gray-900">مطابقة الألوان</h1>

          <div className="w-20"></div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-orange-200 text-center">
            <div className="text-2xl font-black text-orange-600 mb-1">{timeLeft}s</div>
            <div className="text-xs font-bold text-gray-600">الوقت</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-red-200 text-center">
            <div className="text-2xl font-black text-red-600 mb-1">{score}</div>
            <div className="text-xs font-bold text-gray-600">النقاط</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md border-2 border-yellow-200 text-center">
            <div className="text-2xl font-black text-yellow-600 mb-1">{streak}</div>
            <div className="text-xs font-bold text-gray-600">متتالية</div>
          </div>
        </div>
      </div>

      {/* Game Area */}
      <div className="max-w-md mx-auto">
        {/* Color Display */}
        <motion.div
          key={currentColor?.name}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-2xl border-2 border-orange-100 mb-6"
        >
          <p className="text-center text-sm font-bold text-gray-600 mb-4">ما هو لون هذا النص؟</p>
          <div 
            className="text-center text-5xl font-black mb-2"
            style={{ color: displayColor?.nameColor }}
          >
            {displayColor?.name}
          </div>
          <p className="text-center text-xs text-gray-500">اختر اللون الصحيح من الأسفل</p>
        </motion.div>

        {/* Options */}
        <div className="grid grid-cols-2 gap-3">
          {options.map((option, index) => {
            const optionColor = COLORS.find(c => c.name === option);
            return (
              <motion.button
                key={option}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleAnswer(option)}
                className="py-8 rounded-2xl font-black text-xl text-white shadow-lg hover:shadow-xl transition-all active:scale-95"
                style={{ 
                  background: `linear-gradient(135deg, ${optionColor.color}, ${optionColor.nameColor})`
                }}
              >
                {option}
              </motion.button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ColorMatchGame;